import {
  StopwatchIcon,
  CheckCircledIcon,
  CircleBackslashIcon,
  MinusCircledIcon,
  PlusCircledIcon,
  QuestionMarkCircledIcon,
} from "@radix-ui/react-icons";
import { string, z } from "zod";

import { hasMissingSubscriberId } from "@/src/lib/coverage-utils";
import { logger } from "@/src/lib/logging/logger";
import { tryAnyEhrId } from "@/src/lib/resolve-ehr-ids";
import { properCase } from "@/src/lib/string";
import { convertDateStringToUSFormat } from "@/src/lib/utils/date-and-phone-utils";
import { validateObject, ObjectErrors } from "@/src/lib/utils/object-utils";
import { Address } from "@/src/models/address";
import { Appointment } from "@/src/models/appointment";
import { simpleDateSchema } from "@/src/models/common-schema";
import { Coverage } from "@/src/models/coverage";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { PatientAlert } from "@/src/models/patient-alert";
import {
  BinaryPersonGender,
  PersonEthnicity,
  PersonGender,
  PersonRace,
  personDemographicsSchema,
  personSchema,
  personalContactSchema,
} from "@/src/models/person";

export enum PatientStatus {
  Active = "active",
  Inactive = "inactive",
  ON_HOLD = "on_hold",
  TO_SCHEDULE = "to_schedule",
  PENDING_EHR_SYNC = "pending_ehr_sync",
  WELCOME = "welcome",
  VERIFY_ELIGIBILITY = "verify_eligibility",
}

export enum PatientType {
  ESTABLISHED = "Established",
  LEAD = "Lead",
}

export const FIELDS_TO_VALIDATE_FOR_PATIENT = [
  "personalEmail",
  "homePhoneNumber",
  "mobilePhoneNumber",
  "gender",
  "ethnicity",
  "language",
  "race",
];

export const statusIcons: Record<PatientStatus, { icon: React.ForwardRefExoticComponent<any>; color: string }> = {
  [PatientStatus.Active]: { icon: CheckCircledIcon, color: "text-green-700" },
  [PatientStatus.Inactive]: { icon: CircleBackslashIcon, color: "text-gray-500" },
  [PatientStatus.ON_HOLD]: { icon: MinusCircledIcon, color: "text-yellow-600" },
  [PatientStatus.TO_SCHEDULE]: { icon: StopwatchIcon, color: "text-blue-600" },
  [PatientStatus.WELCOME]: { icon: PlusCircledIcon, color: "text-orange-600" },
  [PatientStatus.VERIFY_ELIGIBILITY]: { icon: QuestionMarkCircledIcon, color: "text-teal-600" },
  [PatientStatus.PENDING_EHR_SYNC]: { icon: QuestionMarkCircledIcon, color: "text-yellow-500" },
} as const;

export const patientMinimalSchema = z.object({
  ...personSchema.shape,
  dateOfBirth: simpleDateSchema.nullable(),
});

export const patientSchema = z.object({
  ...personSchema.shape,
  ...personalContactSchema.shape,
  ...personDemographicsSchema.shape,
  preferredPharmacy: string()
    .nullable()
    .describe(
      `The patient's preferred pharmacy look for a field like Pharmacy, Pharmacy Name, or Pharmacy Location hopefully with preferred prepending. If it is not present make this null.
      This is a free text field to allow for any or all of: name, address, or phone number.
      This should explictly try to find a pharmacy only.
      `
    ),
}).describe(`
    A patient is a person who is receiving medical treatment from a provider.
    A patient can have one or more insurance coverages.
    A patient's contact information is usually clustered together sometimes in a demographic section.

    Extract the patient's address from this medical referral document. The patient address may appear under or near headings such as:
    - "Patient Information"
    - "Patient Address"
    - "Demographics"
    - Or near the patient's name, date of birth, or contact details

    Do NOT extract addresses associated with the referring provider or receiving provider. These may appear under headings like:
    - "Referring Provider," "Referring Physician"
    - "Receiving Provider," "Specialist," or "To"
    - "Facility Address" or "Practice Address"
    - "Referral From Information" or "Referral to Information"
  `);

export type Patient = Omit<z.infer<typeof patientSchema>, "homeAddress" | "workAddress"> & {
  ehrId: string | null;
  secondaryEhrId: string | null;
  displayId: string | null;
  status: PatientStatus;
  type: PatientType;
  ehrSearches: Patient[] | null;
  ehrMetadata: string | null;
  homeAddress: Address | null;
  workAddress: Address | null;
  coverages: Coverage[] | null;
  appointments?: Appointment[] | null;
  alerts?: PatientAlert[] | null;
  notFoundInEhr: boolean | null;
  createdInEhr: boolean | null;
  notes: string | null;
  isDuplicate: boolean | null;
} & DatabaseDefaults;

export function getPatientPhoneNumber(patient: Patient): string | null {
  return patient.homePhoneNumber ?? patient.mobilePhoneNumber ?? patient.workPhoneNumber ?? null;
}

export function getFullAddress(patient: Patient): string {
  const address = patient.homeAddress;

  if (!address) return "";

  return [
    address.line1,
    address.line2,
    address.city,
    address.district,
    address.state,
    address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");
}

export function getHomeAddress(patient: Patient): Address | null {
  const homeAddress = patient.homeAddress;

  if (homeAddress) {
    return homeAddress;
  }

  return null;
}

export function getPatientName(patient: Patient): string {
  const { givenName, middleName, familyName } = patient;

  return [properCase(givenName), properCase(middleName), properCase(familyName)].filter(Boolean).join(" ");
}

export function getPatientInitials(patient: Pick<Patient, "givenName" | "familyName"> | null | undefined): string {
  if (!patient) return "";

  const { givenName, familyName } = patient;

  return [givenName, familyName].map((name) => name?.[0]?.toUpperCase() ?? "").join("");
}

export const getPatientDisplayName = (p: Patient) => {
  const name = [p.givenName, p.familyName].filter(Boolean).join(" ");
  const dob = p.dateOfBirth ? `(DOB: ${convertDateStringToUSFormat(p.dateOfBirth)})` : "";

  return `${name} ${dob}`.trim();
};

export const getPatientPotentialMatchDisplayName = (p: Patient) => {
  const name = [p.givenName, p.middleName, p.familyName].filter(Boolean).join(" ");
  const dob = p.dateOfBirth ? `(DOB: ${convertDateStringToUSFormat(p.dateOfBirth)})` : "";
  const displayId = p.displayId ?? "";

  return [`${name} ${dob}`.trim(), displayId].filter(Boolean).join(" · ");
};

export function getPatientPrimaryCoverage(patient: Patient): Coverage | null {
  if (!patient.coverages || patient.coverages.length === 0) return null;

  const activeCoverages = patient.coverages.filter((coverage) => coverage.deletedAt === null);
  const primaryCoverage = activeCoverages.find((coverage) => coverage.order === 1);

  if (primaryCoverage) {
    return primaryCoverage;
  }

  const sortedCoverages = activeCoverages.sort((a, b) =>
    a.order === b.order ? 0 : (a.order ?? Infinity) - (b.order ?? Infinity)
  );

  if (sortedCoverages.length > 1) {
    logger.warn("More than one coverage found with no primary; selecting first option", { patientId: patient.id });
  }

  return sortedCoverages[0] ?? null;
}

/**
 * True when we have no primary subscriber ID on file — either no primary coverage row at all (the
 * payer/plan-only intake flow files coverage in a follow-up call, so the patient can exist with zero
 * coverages) or a primary row whose subscriber ID is blank/"0". Both are "insurance info still
 * needed" for the missing-subscriber-id TE fallback, so the enqueue and EOD paths must agree here.
 */
export function isMissingPrimarySubscriberId(patient: Patient): boolean {
  const coverage = getPatientPrimaryCoverage(patient);

  return !coverage || hasMissingSubscriberId(coverage);
}

export const patientShape: Patient = {
  id: "",
  ehrId: null,
  secondaryEhrId: null,
  displayId: null,
  groupId: "",
  status: PatientStatus.Active,
  type: PatientType.ESTABLISHED,
  ehrSearches: null,
  ehrMetadata: null,
  homeAddress: null,
  workAddress: null,
  givenName: null,
  familyName: null,
  middleName: null,
  gender: PersonGender.UNKNOWN,
  inferredGender: BinaryPersonGender.FEMALE,
  dateOfBirth: null,
  preferredName: null,
  prefix: [],
  suffix: [],
  ethnicity: PersonEthnicity.NOT_PROVIDED,
  language: null,
  homePhoneNumber: null,
  workPhoneNumber: null,
  faxPhoneNumber: null,
  mobilePhoneNumber: null,
  personalEmail: null,
  workEmail: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  coverages: null,
  race: PersonRace.NOT_PROVIDED,
  ssn: null,
  emergencyContactName: null,
  emergencyContactPhoneNumber: null,
  emergencyContactRelationshipToPatient: null,
  preferredPharmacy: null,
  notFoundInEhr: null,
  createdInEhr: null,
  notes: null,
  isDuplicate: null,
};

export type PatientErrors = ObjectErrors<Patient>;
export type PatientFieldKey = keyof Patient | `homeAddress.${keyof Address}`;

/**
 * Validates a Patient object using the generic validateObject helper.
 * If fieldsToValidate is provided, only those fields are validated. Supports nested address fields as 'homeAddress.field'.
 */
export function validatePatient(patient: Patient, fieldsToValidate?: PatientFieldKey[]) {
  return validateObject<Patient>(patientSchema, patient, fieldsToValidate);
}

export function isPatientNotInEhr(patient: Patient): boolean {
  return !tryAnyEhrId(patient) || patient.notFoundInEhr === true;
}
